import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { reservationService } from '../../services/reservationService';
import { agencyService } from '../../services/agencyService';
import { regionService } from '../../services/regionService';
import { vehicleService } from '../../services/vehicleService';
import ReservationBasicInfo from './components/ReservationBasicInfo';
import PassengerInfo from './components/PassengerInfo';
import TransferInfo from './components/TransferInfo';
import ContactInfo from './components/ContactInfo';
import PaymentInfo from './components/PaymentInfo';
import PriceSummary from './components/PriceSummary';
import StepProgressBar from './components/StepProgressBar';
import { FolderMinusIcon } from '@heroicons/react/24/outline';

const ReservationCreate = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentUser: user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [agencies, setAgencies] = useState([]);
  const [regions, setRegions] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);

  // Form verileri
  const [formData, setFormData] = useState({
    // Temel bilgiler
    agencyId: '',
    agencyReference: '',
    fromRegionId: '',
    toRegionId: '',
    hotelName: '',
    secondaryHotelName: '',
    isRoundTrip: false,
    vehicleTypeId: '',
    price: '',
    arrivalPrice: '',
    returnPrice: '',
    currency: 'EUR',
    passengerCount: 1,

    // Ödeme bilgileri
    paymentStatus: 'pending',
    paymentMethod: 'cash',
    paymentNotes: '',
    paidAmount: 0,

    // Yolcu bilgileri
    passengers: [
      {
        firstName: '',
        lastName: '',
        nationality: '',
        identityNumber: '',
        gender: '',
      }
    ],

    // Transfer bilgileri
    transfers: [
      {
        direction: 'arrival', // arrival veya departure
        date: '',
        time: '',
        flightNumber: '',
        pickupLocation: '',
        dropoffLocation: '',
        roomNumber: '',
        babySeatCount: 0,
        childSeatCount: 0,
        collectionAmount: null,
        collectionCurrency: 'EUR',
        driverNote: '',
        operationNote: '',
      }
    ],

    // İletişim bilgileri
    contactDetails: [
      {
        type: 'email',
        value: '',
        isPrimary: true
      },
      {
        type: 'phone',
        value: '',
        isPrimary: true
      }
    ]
  });

  useEffect(() => {
    fetchAgencies();
    fetchRegions();
    fetchVehicleTypes();
  }, [user]); // user değiştiğinde tekrar çağır

  const fetchAgencies = async () => {
    try {

      // Acente kullanıcısı ise sadece kendi acentesini göster
      if (user?.role === 'agency_user' && user?.agency) {
        setAgencies([user.agency]);
        // Acente kullanıcısı için otomatik olarak kendi acentesini seç
        setFormData(prev => ({
          ...prev,
          agencyId: user.agency.id
        }));
        return;
      }

      // Sistem kullanıcıları için tüm acenteleri getir
      const response = await agencyService.getAgencies({ isActive: true });
      if (response && response.success && response.data) {
        setAgencies(response.data.agencies);
      }
    } catch (error) {
      console.error('Error fetching agencies:', error);
      toast.error(t('messages.agencyLoadError'));
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await regionService.getRegions();
      if (response && response.success && response.data) {
        setRegions(response.data.regions);
      }
    } catch (error) {
      console.error('Error fetching regions:', error);
      toast.error(t('messages.regionLoadError'));
    }
  };

  const fetchVehicleTypes = async () => {
    try {
      const response = await vehicleService.getVehicleTypes();
      if (response && response.success && response.data) {
        setVehicleTypes(response.data.vehicleTypes);
      }
    } catch (error) {
      console.error('Error fetching vehicle types:', error);
      toast.error(t('messages.vehicleTypeLoadError'));
    }
  };

  const handleBasicInfoChange = (data) => {
    setFormData(prev => ({
      ...prev,
      ...data
    }));

    // Yolcu sayısı değiştiyse, yolcu listesini güncelle
    if (data.passengerCount !== undefined && data.passengerCount !== formData.passengerCount) {
      const newPassengerCount = parseInt(data.passengerCount);
      const currentPassengers = [...formData.passengers];

      if (newPassengerCount > currentPassengers.length) {
        // Yeni yolcular ekle
        const newPassengers = [...currentPassengers];
        for (let i = currentPassengers.length; i < newPassengerCount; i++) {
          newPassengers.push({
            firstName: '',
            lastName: '',
            nationality: '',
            identityNumber: '',
            gender: '',
          });
        }
        setFormData(prev => ({
          ...prev,
          passengers: newPassengers
        }));
      } else if (newPassengerCount < currentPassengers.length) {
        // Fazla yolcuları kaldır
        setFormData(prev => ({
          ...prev,
          passengers: currentPassengers.slice(0, newPassengerCount)
        }));
      }
    }

    // Gidiş-dönüş seçeneği değiştiyse, transfer listesini güncelle
    if (data.isRoundTrip !== undefined && data.isRoundTrip !== formData.isRoundTrip) {
      if (data.isRoundTrip) {
        // Dönüş transferi ekle
        if (!formData.transfers.some(t => t.direction === 'departure')) {
          setFormData(prev => ({
            ...prev,
            transfers: [
              ...prev.transfers,
              {
                direction: 'departure',
                date: '',
                time: '',
                flightNumber: '',
                pickupLocation: '',
                dropoffLocation: '',
                roomNumber: '',
                babySeatCount: 0,
                childSeatCount: 0,
                collectionAmount: null,
                collectionCurrency: 'EUR',
                driverNote: '',
                operationNote: '',
              }
            ]
          }));
        }
      } else {
        // Dönüş transferini kaldır
        setFormData(prev => ({
          ...prev,
          transfers: prev.transfers.filter(t => t.direction === 'arrival')
        }));
      }
    }
  };

  const handlePassengerInfoChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      passengers: prev.passengers.map((passenger, i) =>
        i === index ? { ...passenger, [field]: value } : passenger
      )
    }));
  };

  const addPassenger = () => {
    setFormData(prev => ({
      ...prev,
      passengers: [...prev.passengers, {
        firstName: '',
        lastName: '',
        nationality: '',
        identityNumber: '',
        gender: '',
      }],
      passengerCount: prev.passengerCount + 1
    }));
  };

  const removePassenger = (index) => {
    if (formData.passengers.length > 1) {
      setFormData(prev => ({
        ...prev,
        passengers: prev.passengers.filter((_, i) => i !== index),
        passengerCount: prev.passengerCount - 1
      }));
    }
  };

  const handleTransferInfoChange = (transfers) => {
    setFormData(prev => ({
      ...prev,
      transfers
    }));
  };

  const handleContactInfoChange = (contactDetails) => {
    setFormData(prev => ({
      ...prev,
      contactDetails
    }));
  };

  const nextStep = () => {
    setCurrentStep(prev => Math.min(prev + 1, 6));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1: // Temel bilgiler
        if (!formData.agencyId) return t('validation.agencyRequired');
        if (!formData.fromRegionId) return t('validation.fromRegionRequired');
        if (!formData.toRegionId) return t('validation.toRegionRequired');
        if (!formData.vehicleTypeId) return t('validation.vehicleTypeRequired');
        if (!formData.price) return t('validation.priceRequired');
        if (formData.passengerCount < 1) return t('validation.passengerCountRequired');
        return null;

      case 2: // Yolcu bilgileri
        for (let i = 0; i < formData.passengers.length; i++) {
          const passenger = formData.passengers[i];
          if (!passenger.firstName) return t('validation.passengerFirstNameRequired', { index: i+1 });
          if (!passenger.lastName) return t('validation.passengerLastNameRequired', { index: i+1 });
        }
        return null;

      case 3: // Transfer bilgileri
        for (let i = 0; i < formData.transfers.length; i++) {
          const transfer = formData.transfers[i];
          if (!transfer.date) {
            return t('validation.transferDateRequired', { index: i+1 });
          }
          if (!transfer.time) {
            return t('validation.transferTimeRequired', { index: i+1 });
          }
          // Tarih formatını kontrol et (YYYY-MM-DD)
          if (!/^\d{4}-\d{2}-\d{2}$/.test(transfer.date)) {
            return t('validation.transferDateFormat', { index: i+1 });
          }
          // Saat formatını kontrol et (HH:MM)
          if (!/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(transfer.time)) {
            return t('validation.transferTimeFormat', { index: i+1 });
          }
        }
        return null;

      case 4: // İletişim bilgileri

        const phoneContact = formData.contactDetails.find(c => c.type === 'phone' && c.isPrimary);


        if (!phoneContact || !phoneContact.value) return t('validation.phoneRequired');
        return null;

      case 5: // Ödeme bilgileri
        if (!formData.paymentStatus) return t('validation.paymentStatusRequired');
        if (!formData.paymentMethod) return t('validation.paymentMethodRequired');
        if (formData.paidAmount < 0) return t('validation.paidAmountNegative');
        return null;

      default:
        return null;
    }
  };

  const handleStepChange = (direction) => {
    if (direction === 'next') {
      const error = validateStep(currentStep);
      if (error) {
        toast.error(error);
        return;
      }
      nextStep();
    } else {
      prevStep();
    }
  };

  const handleSubmit = async () => {
    // Son adımı doğrula
    const error = validateStep(currentStep);
    if (error) {
      toast.error(error);
      return;
    }

    setIsSubmitting(true);
    try {
      // Backend'in beklediği formata dönüştür
      const formattedData = {
        agencyId: formData.agencyId,
        agencyReference: formData.agencyReference,
        fromRegionId: formData.fromRegionId,
        toRegionId: formData.toRegionId,
        hotelName: formData.hotelName, // Otel adını ekleyelim
        secondaryHotelName: formData.secondaryHotelName, // İkinci otel adını ekleyelim
        vehicleTypeId: formData.vehicleTypeId,
        isRoundTrip: formData.isRoundTrip,
        passengerCount: formData.passengerCount,
        price: formData.price,
        arrivalPrice: formData.arrivalPrice,
        returnPrice: formData.returnPrice,
        currency: formData.currency,
        paymentNotes: formData.paymentNotes,
        status: 'pending',

        // Yolcu bilgileri
        passengers: formData.passengers.map(passenger => ({
          firstName: passenger.firstName,
          lastName: passenger.lastName,
          nationality: passenger.nationality,
          identityNumber: passenger.identityNumber,
          birthDate: passenger.birthDate,
          gender: passenger.gender,
        })),

        // Transfer bilgileri
        transfers: formData.transfers.map(transfer => ({
          isReturn: transfer.direction === 'departure',
          transferDate: transfer.date,
          transferTime: transfer.time,
          flightNumber: transfer.flightNumber,
          pickupLocation: transfer.pickupLocation,
          dropoffLocation: transfer.dropoffLocation,
          roomNumber: transfer.roomNumber,
          babySeatCount: transfer.babySeatCount,
          childSeatCount: transfer.childSeatCount,
          driverNote: transfer.driverNote,
          operationNote: transfer.operationNote,
          collectionAmount: transfer.collectionAmount,
          collectionCurrency: transfer.collectionCurrency,
        })),

        // Ödeme bilgileri
        paymentStatus: formData.paymentStatus,
        paymentMethod: formData.paymentMethod,
        paidAmount: formData.paidAmount,
        paymentNotes: formData.paymentNotes,

        // İletişim bilgileri
        contactDetails: formData.contactDetails.map(contact => ({
          type: contact.type,
          value: contact.value,
          isPrimary: contact.isPrimary
        }))
      };

      const response = await reservationService.createReservation(formattedData);

      if (response && response.success) {
        toast.success(t('messages.reservationCreated'));
        navigate(`/reservations/${response.reservation.id}`);
      } else {
        toast.error(response?.message || t('messages.reservationCreateError'));
      }
    } catch (error) {
      console.error('Error creating reservation:', error);
      toast.error(error.response?.data?.message || t('messages.reservationCreateError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <ReservationBasicInfo
            formData={formData}
            onChange={handleBasicInfoChange}
            agencies={agencies}
            regions={regions}
            vehicleTypes={vehicleTypes}
            user={user}
          />
        );
      case 2:
        return (
          <PassengerInfo
            passengers={formData.passengers}
            onChange={handlePassengerInfoChange}
            onAdd={addPassenger}
            onRemove={removePassenger}
          />
        );
      case 3:
        return (
          <TransferInfo
            transfers={formData.transfers}
            isRoundTrip={formData.isRoundTrip}
            onChange={handleTransferInfoChange}
          />
        );
      case 4:
        return (
          <ContactInfo
            contactDetails={formData.contactDetails}
            onChange={handleContactInfoChange}
          />
        );
      case 5:
        return (
          <PaymentInfo
            formData={formData}
            setFormData={setFormData}
          />
        );
      case 6:
        return (
          <PriceSummary
            formData={formData}
            agencies={agencies}
            regions={regions}
            vehicleTypes={vehicleTypes}
          />
        );
      default:
        return null;
    }
  };

  const steps = [t('steps.basicInfo'), t('steps.passengerInfo'), t('steps.transferInfo'), t('steps.contactInfo'), t('steps.paymentInfo'), t('steps.summary')];

  return (
    <div>
      <div className="pb-5 border-b border-gray-200">
        <h1 className="text-2xl font-semibold text-gray-900">{t('reservation.newReservation')}</h1>
      </div>

      <div className="mt-6 bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
        {/* Adım göstergesi */}
        <StepProgressBar currentStep={currentStep} steps={steps} />

        {/* Adım içeriği */}
        <div className="mt-8">
          {renderStepContent()}
        </div>

        {/* Navigasyon butonları */}
        <div className="mt-8 flex justify-between">
          <button
            type="button"
            onClick={() => handleStepChange('prev')}
            disabled={currentStep === 1}
            className={`inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
              currentStep === 1 ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {t('common.back')}
          </button>

          {currentStep < 6 ? (
            <button
              type="button"
              onClick={() => handleStepChange('next')}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {t('common.next')}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? t('common.saving') : t('reservation.createReservation')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReservationCreate;
